import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";

import {
    AccessControlChangesetForStandardAccessRight,
    AdminEditViewAccessRights,
    StandardAccessRight,
    UserIdAndRightForStandardAccessRight,
} from "api/generated/model";

import { ACCESS_RIGHTS, ACCESS_RIGHT_ORDERS } from "./constants";

function formatAccessRight(accessRight: StandardAccessRight): string {
    switch (accessRight) {
        case ACCESS_RIGHTS.ADMIN:
            return t("AccessRights_Admin");
        case ACCESS_RIGHTS.EDIT:
            return t("AccessRights_Edit");
        case ACCESS_RIGHTS.VIEW:
            return t("AccessRights_View");
        default:
            return assertNever(accessRight);
    }
}

function formatAccessRightRole(accessRight: StandardAccessRight): string {
    switch (accessRight) {
        case ACCESS_RIGHTS.ADMIN:
            return t("ControlEntities_AssignUsers_Right_Admin");
        case ACCESS_RIGHTS.EDIT:
            return t("ControlEntities_AssignUsers_Right_Edit");
        case ACCESS_RIGHTS.VIEW:
            return t("ControlEntities_AssignUsers_Right_View");
        default:
            return assertNever(accessRight);
    }
}

function unformatAccessRight(accessRight: string): StandardAccessRight {
    switch (accessRight) {
        case t("AccessRights_Admin"):
            return ACCESS_RIGHTS.ADMIN;
        case t("AccessRights_Edit"):
            return ACCESS_RIGHTS.EDIT;
        case t("AccessRights_View"):
            return ACCESS_RIGHTS.VIEW;
        default:
            throw new Error(
                "Unexpected formatted access right: " + accessRight
            );
    }
}

function formatToUserIdAndRight(
    accessRights: AdminEditViewAccessRights
): UserIdAndRightForStandardAccessRight[] {
    const result: UserIdAndRightForStandardAccessRight[] = [];
    accessRights.admin.forEach((userId) => {
        result.push({ userId, right: "Admin" });
    });
    accessRights.edit.forEach((userId) => {
        result.push({ userId, right: "Edit" });
    });
    accessRights.view.forEach((userId) => {
        result.push({ userId, right: "View" });
    });
    return result;
}

function formatToAdminEditViewAccessRights(
    userIdAndRights: UserIdAndRightForStandardAccessRight[]
): AdminEditViewAccessRights {
    const result: AdminEditViewAccessRights = {
        admin: [],
        edit: [],
        view: [],
    };
    userIdAndRights.forEach(({ userId, right }) => {
        switch (right) {
            case "Admin":
                return result.admin.push(userId);
            case "Edit":
                return result.edit.push(userId);
            case "View":
                return result.view.push(userId);
            default:
                return assertNever(right);
        }
    });
    return result;
}

function buildAccessRightsChangeset(
    oldAccessRightsData: { userId: number; right: StandardAccessRight }[],
    newAccessRightsData: { userId: number; right: StandardAccessRight }[]
): AccessControlChangesetForStandardAccessRight {
    const accessRights: AccessControlChangesetForStandardAccessRight = {};

    const oldAccessRights = oldAccessRightsData.reduce((rights, value) => {
        const { userId, right } = value;
        rights.set(userId, right);
        return rights;
    }, new Map<number, StandardAccessRight>());

    const newAccessRights = newAccessRightsData.reduce((rights, value) => {
        const { userId, right } = value;
        rights.set(userId, right);
        return rights;
    }, new Map<number, StandardAccessRight>());

    // Compare old and new access rights
    oldAccessRights.forEach((right, userId) => {
        const newRight = newAccessRights.get(userId);
        if (newRight === undefined) {
            // Access right has been removed
            accessRights[userId] = null;
        } else if (newRight !== right) {
            // Access right has been updated
            accessRights[userId] = newRight;
        }
    });

    newAccessRights.forEach((right, userId) => {
        if (!oldAccessRights.has(userId)) {
            // Access right has been added
            accessRights[userId] = right;
        }
    });

    return accessRights;
}

function compareAccessRight(a: StandardAccessRight, b: StandardAccessRight) {
    return ACCESS_RIGHT_ORDERS[a] - ACCESS_RIGHT_ORDERS[b];
}

const ALL_ACCESS_RIGHTS: Array<StandardAccessRight> = Object.keys(
    ACCESS_RIGHT_ORDERS
) as Array<StandardAccessRight>;

const accessRightCategoryExtractorId = (accessRight: StandardAccessRight) =>
    `access-right-${accessRight}`;
const isAccessRightCategoryExtractorId = (extractorId: string) =>
    extractorId.startsWith("access-right-");

export {
    formatAccessRight,
    formatAccessRightRole,
    unformatAccessRight,
    formatToUserIdAndRight,
    formatToAdminEditViewAccessRights,
    buildAccessRightsChangeset,
    compareAccessRight,
    ALL_ACCESS_RIGHTS,
    accessRightCategoryExtractorId,
    isAccessRightCategoryExtractorId,
};
